// Render-time companions of the engine shader compiler
// (fluveria-graph shader_template/wgsl): the engine emits value-slot
// descriptors at compile time; TS resolves them against the live ParamStore
// every frame so exposed-parameter edits never recompile the shader.

import type { OutputValue, ParamStore, ParamValue } from '../graph/nodeDefs.js';
import { readParamValue, type ParamGraphSlot } from '../graph/paramKeys.js';
import type { ShapeArcQueryData } from '../sdf/shapeEngine.js';
import type { ArcBounds } from '../sdf/arcSdf.js';

export interface ArcSlot {
  key: string;
  index: number;
  shapeData?: ShapeArcQueryData;
  contourBounds?: ArcBounds;
}

export interface ValueSlot {
  key: string;
  index: number;
  value: [number, number, number, number];
  paramSource?: {
    nodeId: string;
    slot: ParamGraphSlot;
    fallback: OutputValue;
    componentCount: number;
  };
}

function outputValueToComponents(value: ParamValue, componentCount: number): [number, number, number, number] {
  const source = Array.isArray(value) ? value : [value];
  const components: [number, number, number, number] = [0, 0, 0, 0];
  for (let i = 0; i < componentCount; i++) {
    const component = Number(source[i] ?? 0);
    components[i] = Number.isFinite(component) ? component : 0;
  }
  return components;
}

// The slot does not name its layer: the engine emits one module per material
// and shares it between the layers that use it, so which layer a value belongs
// to is the caller's fact, not the module's.
export function resolveValueSlotValue(slot: ValueSlot, params: ParamStore, graphId: string | null | undefined): [number, number, number, number] {
  if (!slot.paramSource) return slot.value;
  const { nodeId, fallback, componentCount } = slot.paramSource;
  return outputValueToComponents(
    readParamValue(graphId, params, nodeId, fallback, slot.paramSource.slot),
    componentCount,
  );
}
